/**
 * Geometrické výpočty nad WGS84. Vše čisté funkce.
 */
import type { LatLng } from './types';

const R_EARTH_KM = 6371.0088;

const toRad = (deg: number): number => (deg * Math.PI) / 180;
const toDeg = (rad: number): number => (rad * 180) / Math.PI;

/** Vzdušná (haversine) vzdálenost v km. */
export function haversineKm(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const la = toRad(a.lat);
  const lb = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(la) * Math.cos(lb) * Math.sin(dLng / 2) ** 2;
  return 2 * R_EARTH_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Bod ve vzdálenosti km od `from` v azimutu bearing (stupně). */
export function destinationPoint(from: LatLng, bearingDeg: number, km: number): LatLng {
  const d = km / R_EARTH_KM;
  const brg = toRad(bearingDeg);
  const la1 = toRad(from.lat);
  const ln1 = toRad(from.lng);
  const la2 = Math.asin(
    Math.sin(la1) * Math.cos(d) + Math.cos(la1) * Math.sin(d) * Math.cos(brg),
  );
  const ln2 =
    ln1 +
    Math.atan2(
      Math.sin(brg) * Math.sin(d) * Math.cos(la1),
      Math.cos(d) - Math.sin(la1) * Math.sin(la2),
    );
  return { lat: toDeg(la2), lng: ((toDeg(ln2) + 540) % 360) - 180 };
}

/** Azimut z a do b ve stupních 0–360. */
export function bearingDeg(a: LatLng, b: LatLng): number {
  const la1 = toRad(a.lat);
  const la2 = toRad(b.lat);
  const dLng = toRad(b.lng - a.lng);
  const y = Math.sin(dLng) * Math.cos(la2);
  const x =
    Math.cos(la1) * Math.sin(la2) - Math.sin(la1) * Math.cos(la2) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

/** Lineární interpolace mezi body (frakce 0–1). */
export function lerpPoint(a: LatLng, b: LatLng, t: number): LatLng {
  return { lat: a.lat + (b.lat - a.lat) * t, lng: a.lng + (b.lng - a.lng) * t };
}

/**
 * Odhadová „přirozená" geometrie mezi body: mírně prohnutá křivka,
 * aby byl odhad vizuálně odlišený od reálné trasy (ta kopíruje ulice).
 */
export function estimatedArc(a: LatLng, b: LatLng, segments = 16): LatLng[] {
  const pts: LatLng[] = [];
  const dist = haversineKm(a, b);
  // Vychýlení kolmo na spojnici, úměrné délce (max ~6 % délky).
  const offsetKm = Math.min(dist * 0.06, 0.8);
  const perp = (bearingDeg(a, b) + 90) % 360;
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const base = lerpPoint(a, b, t);
    const bulge = Math.sin(t * Math.PI) * offsetKm;
    pts.push(bulge > 0.001 ? destinationPoint(base, perp, bulge) : base);
  }
  return pts;
}

/** Délka lomené čáry v km. */
export function polylineLengthKm(pts: LatLng[]): number {
  let sum = 0;
  for (let i = 1; i < pts.length; i++) sum += haversineKm(pts[i - 1], pts[i]);
  return sum;
}

/** Bod na lomené čáře ve frakci t (0–1) podle délky. */
export function pointAlong(pts: LatLng[], t: number): LatLng {
  if (pts.length === 0) return { lat: 0, lng: 0 };
  if (pts.length === 1 || t <= 0) return pts[0];
  if (t >= 1) return pts[pts.length - 1];
  const total = polylineLengthKm(pts);
  if (total === 0) return pts[0];
  let walked = 0;
  const target = total * t;
  for (let i = 1; i < pts.length; i++) {
    const seg = haversineKm(pts[i - 1], pts[i]);
    if (walked + seg >= target && seg > 0) {
      return lerpPoint(pts[i - 1], pts[i], (target - walked) / seg);
    }
    walked += seg;
  }
  return pts[pts.length - 1];
}

/** Dekódování Google/OSRM polyline (precision 5). */
export function decodePolyline(encoded: string): LatLng[] {
  const pts: LatLng[] = [];
  let index = 0;
  let lat = 0;
  let lng = 0;
  while (index < encoded.length) {
    for (const which of [0, 1] as const) {
      let shift = 0;
      let result = 0;
      let byte: number;
      do {
        byte = encoded.charCodeAt(index++) - 63;
        result |= (byte & 0x1f) << shift;
        shift += 5;
      } while (byte >= 0x20);
      const delta = result & 1 ? ~(result >> 1) : result >> 1;
      if (which === 0) lat += delta;
      else lng += delta;
    }
    pts.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }
  return pts;
}

export interface Bounds {
  south: number;
  west: number;
  north: number;
  east: number;
}

/** Obálka kolem bodů s paddingem v km. */
export function boundsAround(points: LatLng[], padKm: number): Bounds {
  let south = Infinity;
  let west = Infinity;
  let north = -Infinity;
  let east = -Infinity;
  for (const p of points) {
    south = Math.min(south, p.lat);
    north = Math.max(north, p.lat);
    west = Math.min(west, p.lng);
    east = Math.max(east, p.lng);
  }
  const latPad = padKm / 110.574;
  const midLat = (south + north) / 2;
  const lngPad = padKm / (111.32 * Math.max(0.1, Math.cos(toRad(midLat))));
  return {
    south: south - latPad,
    west: west - lngPad,
    north: north + latPad,
    east: east + lngPad,
  };
}
